import { useSyncExternalStore } from 'react';
import { accents } from './tokens';
import type { BDAccent } from './types';

// App-wide accent colour. It lives on <html data-accent="…">, and blackdice.css lets that
// override the data-accent="mint" default each .bd page wrapper sets — so pages don't
// need to know about it.

export const ACCENT_OPTIONS: BDAccent[] = ['mint', 'blue', 'violet', 'amber'];

const STORAGE_KEY = 'bdAccent';
const listeners = new Set<() => void>();

function readStored(): BDAccent {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY) as BDAccent | null;
    if (saved && ACCENT_OPTIONS.includes(saved)) return saved;
  } catch {
    // storage blocked — fall back to the default
  }
  return 'mint';
}

let current: BDAccent = readStored();

function apply(accent: BDAccent) {
  const root = document.documentElement;
  root.dataset.accent = accent;
  // for chrome rendered outside a .bd wrapper (e.g. the top loading bar)
  root.style.setProperty('--bd-accent-active', accents[accent].accent);
}

apply(current);

export function setAccent(accent: BDAccent) {
  if (accent === current) return;
  current = accent;
  apply(accent);
  try {
    window.localStorage.setItem(STORAGE_KEY, accent);
  } catch {
    // not persisted; still applied for this session
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void) {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

export function useAccent(): [BDAccent, (accent: BDAccent) => void] {
  const accent = useSyncExternalStore(subscribe, () => current);
  return [accent, setAccent];
}
